import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSponsorTheme } from '@/components/sponsors/themes';
import { normalizeImg } from '@/lib/images';
import type { CommunitySponsorSummary } from '@/types/community';

interface SponsorCardProps {
  sponsor: CommunitySponsorSummary;
  className?: string;
}

function getInitials(name: string | null | undefined) {
  const value = (name || '').trim();
  if (!value) return 'SP';
  const parts = value.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function getDomain(url: string | null | undefined) {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    return parsed.hostname.replace(/^www\./, '');
  } catch (_err) {
    return null;
  }
}

function formatUpdatedAt(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SponsorCard({ sponsor, className = '' }: SponsorCardProps) {
  const handle = sponsor.handle?.trim() || '';
  const [logoFailed, setLogoFailed] = useState(false);
  const theme = getSponsorTheme(sponsor.theme || undefined);
  const rawLogoUrl = sponsor.logo_url?.trim();
  const logoUrl = rawLogoUrl ? normalizeImg(rawLogoUrl) : null;
  const domain = getDomain(sponsor.website_url);
  const updated = formatUpdatedAt(sponsor.updated_at);
  const displayName = sponsor.display_name?.trim() || handle || 'Sponsor';
  const tagline = sponsor.tagline?.trim() || null;
  const subtitle = tagline || (domain ? domain : handle ? `@${handle}` : 'Visit profile');
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const showLogo = Boolean(logoUrl && !logoFailed);
  const logoAlt = displayName ? `${displayName} logo` : 'Sponsor logo';

  return (
    <Link
      to={handle ? `/s/${encodeURIComponent(handle)}` : '#'}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border ${theme.borderClass} bg-white/90 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md ${className}`.trim()}
    >
      <div className="relative flex items-center gap-3 p-4">
        {showLogo ? (
          <img
            src={logoUrl!}
            alt={logoAlt}
            className="h-14 w-14 rounded-xl border border-white/60 bg-white object-cover shadow-sm"
            loading="lazy"
            onError={(event) => {
              setLogoFailed(true);
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className={`flex h-14 w-14 items-center justify-center rounded-xl ${theme.accentClass}`}>
            <span className="text-lg font-semibold">{initials}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-base font-semibold text-cocoa group-hover:text-cocoa/80">
            {displayName}
          </p>
          <p className="mt-1 line-clamp-2 text-sm text-cocoa/60">{subtitle}</p>
        </div>
      </div>
      {updated ? (
        <div className="mt-auto border-t border-sand/60 bg-sand/20 p-3 text-xs text-cocoa/70">
          Updated {updated}
        </div>
      ) : null}
    </Link>
  );
}
